import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { normalizeError } from "@/lib/errors";
import { adminMailto } from "@/lib/adminContact";
import { resolveAthletePhoto } from "@/lib/photoFallback";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Sparkles, Pencil, FileDown, Trophy, Newspaper, Camera, Crown, Activity, Bell, Building2, Flag, Users, ChevronDown, ChevronRight, Settings2, Eye, ShoppingBag, HandHeart } from "lucide-react";
import { toast } from "sonner";
import { EntityLinksManager } from "@/components/EntityLinks";
import { BookmarkletPromoCard } from "@/components/BookmarkletPromoCard";

function OrgRow({ entity, sourceType, sourceLabel, route, Icon }) {
  const [open, setOpen] = useState(false);
  // V9.21 — Sprint UX Simplification :
  // Rendre immédiatement visibles les actions essentielles pour un gestionnaire
  // (surtout pour une Fédération pilote), sans jargon technique.
  const isFederation = sourceType === "federation";
  const isClub = sourceType === "club";
  // Cible du profil public (page de la structure/coach)
  const publicHref = `${route}/${entity.slug || entity.id}`;
  // La page d'édition suit une convention `/route/:id/edit`
  const editHref = `${route}/${entity.id}/edit`;

  const primaryActions = (() => {
    if (isFederation) {
      const cockpitHref = `/mes-federations/${entity.slug || entity.id}`;
      return [
        { to: cockpitHref, icon: Activity, label: "Cockpit Fédération", testid: "fed-cockpit", primary: true },
        { to: publicHref, icon: Eye, label: "Voir la fiche", testid: "fed-view" },
        { to: "/mes-validations", icon: Bell, label: "Demandes d'affiliation", testid: "fed-affiliations" },
        { to: editHref, icon: Settings2, label: "Gérer ma fédération", testid: "fed-manage" },
      ];
    }
    if (isClub) {
      return [
        { to: publicHref, icon: Eye, label: "Voir la fiche", testid: "club-view" },
        { to: `${publicHref}#athletes`, icon: Users, label: "Athlètes affiliés", testid: "club-athletes" },
        { to: "/mes-validations", icon: Bell, label: "Demandes d'affiliation", testid: "club-affiliations" },
        { to: editHref, icon: Settings2, label: "Gérer mon club", testid: "club-manage" },
      ];
    }
    // Coach
    return [
      { to: publicHref, icon: Eye, label: "Voir ma fiche", testid: "coach-view" },
      { to: "/mes-validations", icon: Bell, label: "Demandes d'affiliation", testid: "coach-affiliations" },
      { to: editHref, icon: Settings2, label: "Gérer ma fiche", testid: "coach-manage" },
    ];
  })();

  return (
    <Card className="p-4 space-y-3" data-testid={`dashboard-org-row-${sourceType}-${entity.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-lg bg-[hsl(var(--aito-lagoon)/0.08)] flex items-center justify-center shrink-0">
            <Icon className="h-5 w-5 text-[hsl(var(--aito-lagoon))]" />
          </div>
          <div className="min-w-0">
            <Link
              to={publicHref}
              className="font-display font-semibold text-sm truncate hover:text-[hsl(var(--aito-lagoon))] block"
              data-testid={`dashboard-org-title-${entity.id}`}
            >
              {entity.name || entity.full_name}
            </Link>
            <div className="text-[11px] text-muted-foreground flex items-center gap-1.5 flex-wrap">
              <span>{sourceLabel}</span>
              {entity.discipline && <span>· {entity.discipline}</span>}
              {entity.island && <span>· {entity.island}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className={`grid gap-2 ${primaryActions.length >= 5 ? "grid-cols-2 sm:grid-cols-5" : primaryActions.length === 4 ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"}`}>
        {primaryActions.map(({ to, icon: A, label, testid }) => (
          <Link key={testid} to={to} onClick={(e) => e.stopPropagation()}>
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-center flex-col h-auto py-2 gap-1"
              data-testid={`dashboard-org-${testid}-${entity.id}`}
            >
              <A className="h-4 w-4" />
              <span className="text-[10px] leading-tight text-center whitespace-normal">{label}</span>
            </Button>
          </Link>
        ))}
      </div>

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-center gap-1 text-[11px] text-muted-foreground hover:text-foreground pt-1"
        data-testid={`dashboard-org-toggle-${entity.id}`}
      >
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        {open ? "Masquer les affiliations détaillées" : "Voir les affiliations détaillées"}
      </button>
      {open && (
        <div className="pt-3 border-t">
          <EntityLinksManager sourceType={sourceType} sourceId={entity.id} />
        </div>
      )}
    </Card>
  );
}

function MyOrganisations() {
  const [coaches, setCoaches] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [federations, setFederations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, cl, f] = await Promise.all([
          api.get("/coaches/mine").then((r) => r.data).catch(() => ({ items: [] })),
          api.get("/clubs/mine").then((r) => r.data).catch(() => ({ items: [] })),
          api.get("/federations/mine").then((r) => r.data).catch(() => ({ items: [] })),
        ]);
        if (cancelled) return;
        setCoaches(c.items || []);
        setClubs(cl.items || []);
        setFederations(f.items || []);
      } catch (e) {
        setError(normalizeError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const total = coaches.length + clubs.length + federations.length;
  if (loading) return null;
  if (total === 0) return null;

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="font-display font-semibold">Mes structures</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Fiches et organisations que vous gérez.</p>
        </div>
        <span className="text-xs text-muted-foreground">{total} entité{total > 1 ? "s" : ""} sous ma gestion</span>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="space-y-3">
        {coaches.map((c) => (
          <OrgRow key={`coach-${c.id}`} entity={c} sourceType="coach" sourceLabel="Coach" route="/coachs" Icon={Users} />
        ))}
        {clubs.map((c) => (
          <OrgRow key={`club-${c.id}`} entity={c} sourceType="club" sourceLabel="Club" route="/clubs" Icon={Building2} />
        ))}
        {federations.map((f) => (
          <OrgRow key={`fed-${f.id}`} entity={f} sourceType="federation" sourceLabel="Fédération" route="/federations" Icon={Flag} />
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground italic mt-3">
        Astuce : pour valider les demandes entrantes (athlètes qui te rattachent à ta fiche), ouvre l'<Link to="/mes-validations" className="underline" data-testid="dashboard-link-inbox">inbox de validations</Link>.
      </p>
    </Card>
  );
}

function AthleteRow({ a }) {
  const [campaign, setCampaign] = useState(null);
  const [campaignLoaded, setCampaignLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/support-campaigns/beneficiary/athlete/${a.id}/active`)
      .then((r) => {
        if (!cancelled) setCampaign(r.data?.campaign || null);
      })
      .catch(() => {
        if (!cancelled) setCampaign(null);
      })
      .finally(() => {
        if (!cancelled) setCampaignLoaded(true);
      });
    return () => { cancelled = true; };
  }, [a.id]);

  const downloadPdf = async () => {
    try {
      const res = await api.post(`/ai/sponsor-pdf/athlete/${a.id}`, {}, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a"); link.href = url; link.download = `AITO_PF_Fiche_Sportive_${a.slug}.pdf`; link.click();
      window.URL.revokeObjectURL(url);
      toast.success("Fiche sportive PDF téléchargée.");
    } catch (e) { toast.error("Erreur PDF"); }
  };
  const pendingSugg = (a.suggestions || []).filter((s) => s.status === "pending" || s.status === "rights_check").length;

  return (
    <Card className="p-5 space-y-4" data-testid="dashboard-my-athlete-row">
      {/* V11.18 — Alerte "Ajoute ta photo" (bloque l'apparition dans la UNE) */}
      {!a.photo_url && (
        <div
          className="flex items-center gap-3 p-3 rounded-lg border-2 border-dashed border-[hsl(var(--aito-gold))] bg-[hsl(var(--aito-gold))]/10"
          data-testid="dashboard-missing-photo-prompt"
        >
          <Camera className="h-5 w-5 text-[hsl(var(--aito-gold))] shrink-0" />
          <div className="flex-1 text-xs">
            <div className="font-semibold">Ta fiche n'a pas encore de photo</div>
            <div className="text-muted-foreground">Ajoute-la pour apparaître dans les <strong>Athlètes à la UNE</strong> sur l'accueil.</div>
          </div>
          <Link to={`/athletes/${a.id}/edit`}>
            <Button size="sm" className="bg-[hsl(var(--aito-gold))] text-black hover:bg-[hsl(var(--aito-gold))]/80 shrink-0" data-testid="dashboard-add-photo-cta">
              Ajouter
            </Button>
          </Link>
        </div>
      )}
      <div className="flex items-start gap-4">
        <img src={resolveAthletePhoto(a)} alt={a.full_name} className={`h-16 w-16 rounded-lg bg-muted ${a.photo_url ? "object-cover" : "object-cover"}`} />
        <div className="flex-1 min-w-0">
          <Link to={`/athletes/${a.slug}`} className="font-display text-lg font-semibold hover:text-[hsl(var(--aito-lagoon))]">{a.full_name}</Link>
          <div className="text-xs text-muted-foreground mt-0.5">{a.main_discipline || (a.disciplines || []).join(" · ") || "—"}</div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Badge variant={a.published ? "default" : "secondary"} className={a.published ? "bg-[hsl(var(--success))] text-white" : ""}>{a.published ? "Publié" : "Brouillon"}</Badge>
            {a.confidence != null && <Badge variant="outline">Confiance {Math.round((a.confidence || 0) * 100)}%</Badge>}
            {pendingSugg > 0 && <Badge className="bg-[hsl(var(--aito-energy))] text-black"><Bell className="h-3 w-3 mr-1" />{pendingSugg} suggestion{pendingSugg > 1 ? "s" : ""}</Badge>}
          </div>
        </div>
      </div>

      {/* V9.21 — Sprint UX Simplification : 3 actions principales par fiche
          (Voir / Gérer / + Ajouter). Les actions détaillées sont regroupées
          derrière un menu pour éviter la surcharge, surtout sur mobile. */}
      <div className="grid grid-cols-3 gap-2 pt-2 border-t">
        <Link to={`/athletes/${a.slug}`} className="min-w-0">
          <Button variant="outline" size="sm" className="w-full justify-center" data-testid="dashboard-view-athlete">
            <Eye className="h-4 w-4 mr-1.5" />Voir ma fiche
          </Button>
        </Link>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="w-full justify-center" data-testid="dashboard-manage-athlete">
              <Settings2 className="h-4 w-4 mr-1.5" />Gérer ma fiche
              <ChevronDown className="h-3.5 w-3.5 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground font-normal">Gérer ma fiche</DropdownMenuLabel>
            <DropdownMenuItem asChild>
              <Link to={`/athletes/${a.id}/edit`} data-testid="dashboard-edit-athlete">
                <Pencil className="h-4 w-4 mr-2" /> Modifier ma fiche
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={`/athletes/${a.id}/edit?tab=settings`} data-testid="dashboard-athlete-settings">
                <Settings2 className="h-4 w-4 mr-2" /> Paramètres & confidentialité
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={downloadPdf} data-testid="dashboard-regenerate-pdf">
              <FileDown className="h-4 w-4 mr-2 text-[hsl(var(--aito-gold))]" /> Exporter ma fiche en PDF
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" className="w-full justify-center bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="dashboard-add-menu">
              <Plus className="h-4 w-4 mr-1.5" />Ajouter
              <ChevronDown className="h-3.5 w-3.5 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground font-normal">Ajouter à mon parcours</DropdownMenuLabel>
            <DropdownMenuItem asChild>
              <Link to={`/athletes/${a.id}/edit?tab=palmares`} data-testid="dashboard-add-result">
                <Trophy className="h-4 w-4 mr-2" /> Résultat
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={`/athletes/${a.id}/edit?tab=palmares`} data-testid="dashboard-add-achievement">
                <Crown className="h-4 w-4 mr-2" /> Titre / distinction
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={`/athletes/${a.id}/edit?tab=media`} data-testid="dashboard-add-photo">
                <Camera className="h-4 w-4 mr-2" /> Photo / média
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={`/athletes/${a.id}/edit?tab=network`} data-testid="dashboard-add-sponsor">
                <Crown className="h-4 w-4 mr-2" /> Sponsor / partenaire
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={`/athletes/${a.id}/edit?tab=sport`} data-testid="dashboard-add-discipline">
                <Activity className="h-4 w-4 mr-2" /> Discipline
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* V9.22 — Campagne de soutien (T-shirt AITO.PF) */}
      {campaignLoaded && (
        <div className="pt-3 border-t" data-testid={`dashboard-support-campaign-${a.id}`}>
          {campaign ? (
            <div className="flex flex-col sm:flex-row sm:items-start gap-3">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="h-10 w-10 rounded-md bg-[hsl(var(--aito-gold))]/15 flex items-center justify-center text-[hsl(var(--aito-gold))] shrink-0">
                  <ShoppingBag className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">Campagne de soutien active</div>
                  <div className="text-xs text-muted-foreground">
                    {Math.min(20, Number(campaign.total_preorders_all_tranches || 0))} / 20 précommandes ·
                    {" "}{(campaign.tshirt_price_xpf || 4500).toLocaleString("fr-FR")} XPF
                  </div>
                </div>
              </div>
              <Link to={`/soutien/${campaign.slug}`} className="w-full sm:w-auto shrink-0">
                <Button variant="outline" size="sm" className="w-full sm:w-auto" data-testid="dashboard-view-campaign">
                  <Eye className="h-3.5 w-3.5 mr-1" /> Voir ma campagne
                </Button>
              </Link>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-start gap-3">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="h-10 w-10 rounded-md bg-[hsl(var(--aito-lagoon))]/10 flex items-center justify-center text-[hsl(var(--aito-lagoon))] shrink-0">
                  <ShoppingBag className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">Campagne de soutien</div>
                  <div className="text-xs text-muted-foreground">
                    Finance ton projet sportif grâce à une campagne de précommandes d'un T-shirt AITO.PF à ton nom.
                  </div>
                </div>
              </div>
              <a
                href={adminMailto({
                  subject: "Demande de campagne de soutien — " + (a.full_name || ""),
                  body: "Bonjour,\n\nJe souhaite lancer une campagne de soutien T-shirt AITO.PF pour financer mon projet sportif.\n\nMerci de me contacter pour en savoir plus.\n\nAthlète : " + (a.full_name || "") + "\nFiche : /athletes/" + (a.slug || a.id),
                })}
                className="w-full sm:w-auto shrink-0"
              >
                <Button size="sm" className="w-full sm:w-auto bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="dashboard-request-campaign">
                  <HandHeart className="h-3.5 w-3.5 mr-1" /> Demander ma campagne
                </Button>
              </a>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export default function DashboardPage() {
  const { user, loading: authLoading, updateFavorites } = useAuth();
  const nav = useNavigate();
  const [disciplines, setDisciplines] = useState([]);
  const [favorites, setFavorites] = useState(user?.favorite_disciplines || []);
  const [myAthletes, setMyAthletes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (!authLoading && !user) nav("/connexion"); }, [user, authLoading, nav]);
  useEffect(() => { setFavorites(user?.favorite_disciplines || []); }, [user]);
  useEffect(() => { api.get("/disciplines").then((r) => setDisciplines(r.data.items || [])); }, []);
  useEffect(() => {
    if (!user) return;
    setLoading(true);
    api.get("/athletes/mine").then((r) => setMyAthletes(r.data.items || [])).finally(() => setLoading(false));
  }, [user]);

  const toggleFav = (slug) => setFavorites((cur) => cur.includes(slug) ? cur.filter((d) => d !== slug) : [...cur, slug]);
  const saveFav = async () => { await updateFavorites(favorites); toast.success("Préférences sauvegardées."); };

  if (!user) return null;

  return (
    <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-8">
      {/* V9.21 — Sprint UX Simplification : la déconnexion vit désormais uniquement
          dans le menu avatar du header (évite le doublon). */}
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold">Mon espace</h1>
        <p className="text-sm text-muted-foreground mt-1">Bonjour {user.name || user.email}. Bienvenue sur AITO.PF.</p>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-4">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div>
                <h2 className="font-display font-semibold text-lg">Mon parcours sportif</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Ma fiche, mes résultats, ma progression.</p>
              </div>
              <Link to="/creer-ma-fiche"><Button size="sm" variant="outline" data-testid="dashboard-create-new"><Plus className="h-4 w-4 mr-1" />Créer une fiche</Button></Link>
            </div>
            {loading ? <div className="space-y-3"><div className="h-24 bg-muted animate-pulse rounded-lg" /></div> :
              myAthletes.length === 0 ? (
                <p className="text-sm text-muted-foreground">Vous n'avez pas encore de fiche athlète. Lancez l'assistant pour commencer à documenter votre parcours.</p>
              ) : (
                <div className="space-y-3">{myAthletes.map((a) => <AthleteRow key={a.id} a={a} />)}</div>
              )}
          </Card>

          <MyOrganisations />

          <Card className="p-5">
            <h2 className="font-display font-semibold mb-2">Disciplines favorites</h2>
            <p className="text-xs text-muted-foreground mb-3">Personnalisez votre fil d'actualités.</p>
            <div className="flex flex-wrap gap-1.5">
              {disciplines.map((d) => {
                const active = favorites.includes(d.slug);
                return (
                  <button key={d.slug} onClick={() => toggleFav(d.slug)} data-testid={`fav-${d.slug}`}
                    className={`px-3 py-1.5 text-xs rounded-full border ${active ? "bg-[hsl(var(--aito-lagoon))] text-black border-transparent" : "hover:bg-muted"}`}>
                    {d.name}
                  </button>
                );
              })}
            </div>
            <Button onClick={saveFav} size="sm" className="mt-4 bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="dashboard-save-favorites">Sauvegarder</Button>
          </Card>
        </div>

        <aside className="lg:col-span-4 space-y-3">
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-2"><Sparkles className="h-5 w-5 text-[hsl(var(--aito-lagoon))]" /><h3 className="font-display font-semibold">Action rapide</h3></div>
            <p className="text-xs text-muted-foreground mb-3">Créez une nouvelle fiche ou ajoutez du contenu à vos fiches existantes.</p>
            <Link to="/creer-ma-fiche"><Button className="w-full bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]">Créer une fiche</Button></Link>
          </Card>
          <BookmarkletPromoCard />
          <Card className="p-5">
            <h3 className="font-display font-semibold mb-2">Rôles</h3>
            <div className="flex flex-wrap gap-1.5">{(user.roles || []).map((r) => <Badge key={r} variant="secondary">{r}</Badge>)}</div>
          </Card>
        </aside>
      </div>
    </div>
  );
}
